import store from '../../../store/store';
import { ActionStatus } from '../../../common/constants/action-status';
import { StoreState } from '../../../store/store.interface';
import environment from '../../../common/environment';
import { getAllWishlistMagento, getWishlistByWishlistIdMagento } from '../../../common/constants/shop-api';
import { html, nothing, TemplateResult } from 'lit';
import { isCustomerLoginSuccess } from '../../customer/customer';
import { loading } from '../../../common/constants/api-constants';
import {
  getAddProductToWishListStatus,
  getCreateWishListStatus,
  getDeleteWishListStatus,
  getEditWishListStatus,
  getRemoveProductFromWishListStatus,
  getWishListsStatus,
  getWishListStatus,
} from '../store/wish-list.selector';
import { WishListDropdownConstants } from '../constants/wish-list-dropdown-constants';
import { mapWishListItemToProductCardData } from '../mapping/wish-list-product-card.mapping';
import { WishListItem, WishList, WishListAnalyticsObject } from '../interfaces/wish-list-items.interface';
import '../../toast/toast';
import { getBulkAddToCartStatus } from '../../cart/shop-cart/store/shop-cart.selectors';
import { mapWishListToWishListAnalyticsObject } from '../mapping/wish-list-analytics-mapping';

const MAX_WISH_LISTS_LIMIT = 30;

export const WISH_LIST_SHARING_CODE_URL_PARAM = 'sharedWishList';

export const isAddProductToWishListSuccess = (): boolean => {
  return getAddProductToWishListStatus(store.getState() as StoreState) === ActionStatus.succeeded;
};

export const isRemoveProductFromWishListLoading = (): boolean => {
  return getRemoveProductFromWishListStatus(store.getState() as StoreState) === ActionStatus.loading;
};

export const isRemoveProductFromWishListSuccess = (): boolean => {
  return getRemoveProductFromWishListStatus(store.getState() as StoreState) === ActionStatus.succeeded;
};

export const isWishListsLoadingOrIdle = (): boolean => {
  return [ActionStatus.loading, ActionStatus.idle].includes(getWishListsStatus(store.getState() as StoreState));
};

export const isWishListsSuccess = (): boolean => {
  return getWishListsStatus(store.getState() as StoreState) === ActionStatus.succeeded;
};

export const isWishListsFailure = (): boolean => {
  return getWishListsStatus(store.getState() as StoreState) === ActionStatus.failed;
};

export const isWishListLoadingOrIdle = (): boolean => {
  return [ActionStatus.loading, ActionStatus.idle].includes(getWishListStatus(store.getState() as StoreState));
};

export const isWishListSuccess = (): boolean => {
  return getWishListStatus(store.getState() as StoreState) === ActionStatus.succeeded;
};

export const isWishListFailure = (): boolean => {
  return getWishListStatus(store.getState() as StoreState) === ActionStatus.failed;
};

export const isCreateWishListLoading = (): boolean => {
  return getCreateWishListStatus(store.getState() as StoreState) === ActionStatus.loading;
};

export const isCreateWishListSuccess = (): boolean => {
  return getCreateWishListStatus(store.getState() as StoreState) === ActionStatus.succeeded;
};

export const isCreateWishListFailure = (): boolean => {
  return getCreateWishListStatus(store.getState() as StoreState) === ActionStatus.failed;
};

export const isDeleteWishListSuccess = (): boolean => {
  return getDeleteWishListStatus(store.getState() as StoreState) === ActionStatus.succeeded;
};

export const isEditWishListLoading = (): boolean => {
  return getEditWishListStatus(store.getState() as StoreState) === ActionStatus.loading;
};

export const isEditWishListSuccess = (): boolean => {
  return getEditWishListStatus(store.getState() as StoreState) === ActionStatus.succeeded;
};

export const isEditWishListFailure = (): boolean => {
  return getEditWishListStatus(store.getState() as StoreState) === ActionStatus.failed;
};

export const isBulkAddToCartLoading = (): boolean => {
  return getBulkAddToCartStatus(store.getState() as StoreState) === ActionStatus.loading;
}

export const isBulkAddToCartSuccess = (): boolean => {
  return getBulkAddToCartStatus(store.getState() as StoreState) === ActionStatus.succeeded;
}

export const isBulkAddToCartFailed = (): boolean => {
  return getBulkAddToCartStatus(store.getState() as StoreState) === ActionStatus.failed;
}

export const wishListDropdownButtons = (): string[] => {
  return [
    WishListDropdownConstants.SHARE,
    WishListDropdownConstants.EDIT_NAME,
    WishListDropdownConstants.DOWNLOAD,
    WishListDropdownConstants.DELETE,
  ];
};

export const wishListReviewDropdownButtons = (): string[] => {
  return [
    WishListDropdownConstants.SHARE,
    WishListDropdownConstants.EDIT_NAME,
    WishListDropdownConstants.DOWNLOAD,
    WishListDropdownConstants.DELETE,
  ];
};

export const sharedWishlistIconButtons = [WishListDropdownConstants.DOWNLOAD, WishListDropdownConstants.SHARE];

export const buttonLabelToAnalyticsEvent = (label) => label.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');

export const downloadOptionToAnalyticsLabel = (option) => {
  const spacedStr = option.replace(/_/g, ' ');
  return spacedStr.replace(/\b\w/g, char => char.toUpperCase());
}

export function getLimitErrorMessage() {
  return html`
    You've hit the maximum of ${MAX_WISH_LISTS_LIMIT} lists. To create a new list, delete a list you no longer needed.
  `;
}

export function getAllWishlistUrlMagento(state: StoreState): string {
  if (isCustomerLoginSuccess(state)) {
    return getAllWishlistMagento();
  } else {
    return '';
  }
}

export function getWishListItemUrl(isbn: string): string {
  return environment().mheducationUrl.concat('/prek-12/product/').concat(isbn).concat('.html');
}

export function getWishlistByWhislistIdUrlMagento(state: StoreState, wishListId: number): string {
  if (isCustomerLoginSuccess(state)) {
    return getWishlistByWishlistIdMagento(wishListId);
  } else {
    return '';
  }
}

export function _renderLoadingTemplate() {
  return html`
    <span class="visually-hidden">Loading wish list</span>
    ${loading({ width: '100%', height: '5rem', m: '' })}
    <br />
    <span class="visually-hidden">Loading wish list</span>
    ${loading({ width: '100%', height: '5rem', m: '' })}
    <br />
    <span class="visually-hidden">Loading wish list</span>
    ${loading({ width: '100%', height: '5rem', m: '' })}
    <br />
    <span class="visually-hidden">Loading wish list</span>
    ${loading({ width: '100%', height: '5rem', m: '' })}
  `;
}

function retainQueryParams() {
  const queryParams = new URL(window.location.href).searchParams;
  queryParams.delete('wishListId');
  return queryParams?.toString();
}

export function getWishListItemsURL(wishListId: number | undefined): string {
  const queryString = retainQueryParams();
  return environment()
    .mheducationUrl.concat('/account/wish-list-items.html/')
    .concat(`?${queryString}&wishListId=${wishListId}`);
}

export function getSharedWishListUrl(sharingCode: string): string {
  return environment()
    .mheducationUrl.concat('/shared/wish-list.html')
    .concat(`?${WISH_LIST_SHARING_CODE_URL_PARAM}=${sharingCode}`);
}

export function redirectToWishListItemsURL(wishListId: number | undefined): string {
  return getWishListItemsURL(wishListId);
}

export function getGenericErrorMessage() {
  return html` <span>The action you tried to take could not be completed. Please try again in a few moments.</span> `;
}

export function renderWishListCard(wishList: WishList, wishListItem: WishListItem, isReadOnly = false) {
  return html`
    <mhecomm-wish-list-item-card-display
      .productCardData="${mapWishListItemToProductCardData(wishListItem)}"
      .wishList="${wishList}"
      .isReadOnly="${isReadOnly}"
    >
    </mhecomm-wish-list-item-card-display>
  `;
}

export function renderWishListItems(wishList: WishList, isReadOnly = false) {
  return html`
    ${wishList.wishlist_items?.length
      ? wishList.wishlist_items?.map((wishListItem) => renderWishListCard(wishList, wishListItem, isReadOnly))
      : nothing}
  `;
}

export function renderNoWishListItemsTemplate({ title, message }: { title: string; message: string }): TemplateResult {
  return html`
    <div>
      <img
        src="/uxt/static/external/misc/no-wish-list-items.svg"
        class=" w-15 h-15"
        alt="No-Wishlist-Items"
        aria-label="No-Wishlist-Items"
      />
    </div>
    <h1 aria-label="${title}">${title}</h1>
    <p aria-label="${message}">
      ${message}
    </p>
  `;
}

/**
 * Redirects the user to the My Lists page.
 */
export function redirectToMyLists() {
  const queryString = retainQueryParams();
  const url = `${environment().mheducationUrl.concat('/account.html')}?${queryString}#myLists`;

  window.location.assign(url);
}

export function getWishlistShareLinkOffCanvasTitle(wishListName: string) {
  return html`
    <span>
      Share
      <span class="fw-bold">${wishListName}</span>
    </span>
  `;
}

export function hasWishListProducts(wishList: WishList): boolean {
  if (!wishList || !wishList?.wishlist_items) {
    return false;
  }

  return wishList.wishlist_items.length > 0;
}

export function getWisListsAnalyticsObjectData(wishLists: WishList[]): WishListAnalyticsObject[] {
  return wishLists.map((wishList) => mapWishListToWishListAnalyticsObject(wishList));
}